// File: src/api/documents.js
import { request } from './client'; 

export const documentService = {
  
  // --- DOCUMENT OPERATIONS ---
  getDocuments: () => request('/documents'),
  
  updateDocument: (id, formData) => 
    request(`/documents/${id}`, { method: 'PUT', body: formData }),
  
  updateDocumentMetadata: (data) => 
    request(`/documents/${data.id}`, { method: 'PUT', body: data }),

  // --- S3 INTEGRATION START ---

  /**
   * Step 1: Get the Presigned URL from your backend
   * @param {string} fileName - The name of the file
   * @param {string} fileType - The MIME type (e.g., 'image/png', 'application/pdf')
   */
  getPresignedUploadUrl: (fileName, fileType) => 
    request(`/documents/s3-upload-url?fileName=${encodeURIComponent(fileName)}&fileType=${encodeURIComponent(fileType)}`, { 
      method: 'GET' 
    }),

  /**
   * Step 2: Upload directly to S3
   */
  uploadToS3: async (presignedUrl, file) => {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!response.ok) {
      throw new Error('Failed to upload to S3');
    }
    return true;
  },

  /**
   * Step 3: Finalize the record in DB
   */
  createDocumentRecord: (documentData) => 
    request('/documents', { method: 'POST', body: documentData }),

  // --- S3 INTEGRATION END ---
  
  // Legacy upload (keep if you still support local server uploads)
  uploadDocument: (formData) => 
    request('/documents/upload', { method: 'POST', body: formData }),
  
  deleteDocument: async (id) => {
    try {
      await request(`/documents/${id}`, { method: 'DELETE' });
      return true;
    } catch (error) {
      if (error.status === 404) return true;
      throw error;
    }
  },
  
  // Updated Download: Handles S3 Presigned GET URLs or Direct Downloads
  downloadDocument: async (id, fileName) => {
    // First, ask backend for the download URL (could be S3 signed URL or local stream)
    const response = await request(`/documents/${id}/download-url`, { method: 'GET' });
    
    if (response.url) {
      // If backend returns a direct S3 URL, open it
      const a = document.createElement('a');
      a.href = response.url;
      a.download = fileName || 'document';
      a.target = '_blank'; // Safer for S3 links
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Fallback to blob download if your backend streams the file
      const blob = await request(`/documents/${id}/download`, { method: 'GET', isBlob: true });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  },
  
  // --- NEW: Archive Document ---
  archiveDocument: async (id) => {
    return request(`/documents/${id}/archive`, { method: 'POST' });
  },
  unarchiveDocument: async (id) => {
    return request(`/documents/${id}/unarchive`, { method: 'POST' });
  },

  exportIndex: async () => {
    // Request the file as a blob
    const blob = await request('/documents/export-index', { method: 'GET', isBlob: true });
    
    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Document_Index_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
